import { Button } from "antd";
import { ChevronRight, Flame, LogOut } from "lucide-react";

import { cn } from "@/lib/cn";
import type { BreadcrumbItem } from "@/routes/menuConfig";

interface TopHeaderProps {
  collapsed: boolean;
  breadcrumbs: BreadcrumbItem[];
  displayName: string;
  roleName: string;
  avatarText: string;
  onToggleCollapse: () => void;
  onLogout: () => void;
}

/** 顶栏：面包屑与用户信息 */
export const TopHeader = ({
  collapsed,
  breadcrumbs,
  displayName,
  roleName,
  avatarText,
  onToggleCollapse,
  onLogout,
}: TopHeaderProps) => {
  return (
    <header className="sticky top-0 z-10 h-14 shrink-0 overflow-hidden">
      <div className="absolute inset-0 bg-linear-to-r from-slate-50/80 via-white to-orange-50/30" />
      <div className="absolute bottom-0 inset-x-0 h-[2px] bg-linear-to-r from-orange-400/60 via-amber-400 to-transparent" />
      <div className="absolute inset-0 backdrop-blur-md bg-white/70" />
      <div className="relative h-full px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {collapsed && (
            <Button
              type="text"
              icon={<ChevronRight className="w-4 h-4" />}
              onClick={onToggleCollapse}
              className="text-slate-400 hover:text-slate-600! h-8 w-8 rounded-lg"
            />
          )}
          <nav className="flex items-center gap-1.5 text-sm">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Flame className="w-3.5 h-3.5 text-orange-500" />
              <span className="font-medium">炽竞</span>
            </div>
            {breadcrumbs.map((crumb, i) => (
              <span key={`${crumb.name}-${i}`} className="flex items-center gap-1.5">
                <ChevronRight className="w-3 h-3 text-slate-300" />
                {crumb.href ? (
                  <span className="text-slate-800 font-semibold">{crumb.name}</span>
                ) : (
                  <span className="text-slate-400">{crumb.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-px h-5 bg-linear-to-b from-transparent via-slate-200 to-transparent mr-1" />
          <div
            className={cn(
              "flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl hover:bg-white/60 cursor-pointer group transition-all duration-200",
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-linear-to-br from-orange-500 via-amber-500 to-yellow-400 flex items-center justify-center text-[11px] font-bold text-white shadow-sm shadow-orange-500/25 ring-2 ring-orange-100/80">
              {avatarText}
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-[12.5px] font-semibold text-slate-700 leading-tight group-hover:text-orange-600 transition-colors">
                {displayName}
              </span>
              <span className="text-[10px] text-slate-400 leading-tight">{roleName}</span>
            </div>
            <LogOut
              className="w-3.5 h-3.5 text-slate-300 group-hover:text-orange-400 transition-colors hidden sm:block"
              onClick={onLogout}
            />
          </div>
        </div>
      </div>
    </header>
  );
};
